import is from '@sindresorhus/is';
import clsx from 'clsx';
import { format, type Locale } from 'date-fns';
import type { FunctionComponent } from 'react';

import { useApplicationContext } from '~/shared/context/use-application-context';
import { useTranslate } from '~/shared/context/use-translate';
import type { Translate } from '~/shared/internationalization/translation';

import type { GrowthCheckpoint, GrowthCheckpointMark, GrowthGoal, GrowthMeasure } from '../../data-contract/growth';
import { StatusIndicator } from '../../job-architecture-v2';

export type GrowthTone =
    | 'draft'
    | 'error'
    | 'info'
    | 'muted'
    | 'orange'
    | 'pending'
    | 'published'
    | 'reviewed'
    | 'success'
    | 'warning';

// Goal states reuse the job architecture review colours: draft grey, reviewed blue, published green, pending orange.
const toneTextClasses: Record<GrowthTone, string> = {
    draft: 'text-status-draft',
    error: 'text-status-error-text',
    info: 'text-status-info-text',
    muted: 'text-text-muted',
    orange: 'text-status-orange-text',
    pending: 'text-orange-800',
    published: 'text-status-published',
    reviewed: 'text-status-reviewed',
    success: 'text-status-success-text',
    warning: 'text-status-warning-text',
};

export const htmlToPlainText = (html: string): string =>
    (
        new DOMParser().parseFromString(html.replaceAll(/<\/(p|li|h[1-6])>|<br\s*\/?>/giu, '$& '), 'text/html').body
            .textContent ?? ''
    )
        .replaceAll(/\s+/gu, ' ')
        .trim();

/** A due date is a calendar day; it is overdue once that day has ended in local time. */
export const isPastDue = (isoDay: string, now: number = Date.now()): boolean => {
    const [year = 0, month = 1, day = 1] = isoDay.split('-').map(Number);

    return new Date(year, month - 1, day + 1).getTime() <= now;
};

const CALENDAR_DAY = /^\d{4}-\d{2}-\d{2}$/u;

// A bare calendar day is shown as that day in local time; `new Date('YYYY-MM-DD')` would read it as UTC midnight.
const toLocalDate = (isoDate: string): Date => {
    if (!CALENDAR_DAY.test(isoDate)) {
        return new Date(isoDate);
    }
    const [year = 0, month = 1, day = 1] = isoDate.split('-').map(Number);

    return new Date(year, month - 1, day);
};

export const formatDay = (isoDate: string, locale: Locale): string => format(toLocalDate(isoDate), 'PP', { locale });

/** Formats calendar days in the UI language, not the browser locale. */
export const useFormatDay = (): ((isoDate: string) => string) => {
    const {
        applicationState: { language },
    } = useApplicationContext();

    return (isoDate) => formatDay(isoDate, language.date.locale);
};

export const goalStatus = (
    goal: GrowthGoal,
    translate: Translate,
): { readonly label: string; readonly tone: GrowthTone } => {
    switch (goal.state) {
        case 'draft':
            return is.nonEmptyString(goal.returnComment)
                ? { label: translate('pages.growth.states.returned'), tone: 'warning' }
                : { label: translate('pages.growth.states.draft'), tone: 'draft' };
        case 'waiting-for-agreement':
            return { label: translate('pages.growth.states.waiting'), tone: 'pending' };
        case 'in-review':
            return is.nullOrUndefined(goal.outcome)
                ? { label: translate('pages.growth.states.in-review'), tone: 'reviewed' }
                : { label: translate('pages.growth.detail.outcome-proposed'), tone: 'reviewed' };
        case 'done':
            return { label: translate('pages.growth.states.done'), tone: 'published' };
        case 'open':
            if (goal.isStale) {
                return { label: translate('pages.growth.states.stale'), tone: 'warning' };
            }

            return goal.visibility === 'private'
                ? { label: translate('pages.growth.states.private'), tone: 'draft' }
                : { label: translate('pages.growth.states.agreed'), tone: 'published' };
    }
};

export const markLabel = (mark: GrowthCheckpointMark, translate: Translate): string => {
    switch (mark) {
        case 'changed':
            return translate('pages.growth.checkpoint-marks.changed');
        case 'needs-support':
            return translate('pages.growth.checkpoint-marks.needs-support');
        case 'on-track':
            return translate('pages.growth.checkpoint-marks.on-track');
    }
};

export const StatusDot: FunctionComponent<{ readonly label: string; readonly tone: GrowthTone }> = ({
    label,
    tone,
}) => (
    <StatusIndicator
        className={toneTextClasses[tone]}
        icon={<span className="size-s rounded-full bg-current" />}
        size="xs"
        weight="medium"
    >
        {label}
    </StatusIndicator>
);

export const GoalStatusBadge: FunctionComponent<{ readonly goal: GrowthGoal }> = ({ goal }) => {
    const translate = useTranslate();
    const { label, tone } = goalStatus(goal, translate);

    return <StatusDot label={label} tone={tone} />;
};

export const nextOpenCheckpoint = (goal: GrowthGoal): GrowthCheckpoint | undefined =>
    goal.checkpoints.find(({ doneAt }) => is.nullOrUndefined(doneAt));

export const measureProgress = (measure: GrowthMeasure): number | undefined => {
    switch (measure.type) {
        case 'number': {
            const span = measure.target - measure.start;

            return span === 0 ? 1 : (measure.current - measure.start) / span;
        }
        case 'percent':
            return measure.current / 100;
        case 'milestones':
            return measure.milestones.length === 0
                ? 0
                : measure.milestones.filter(({ isDone }) => isDone).length / measure.milestones.length;
        case 'done':
            return undefined;
    }
};

export const measureLabel = (measure: GrowthMeasure, translate: Translate): string => {
    switch (measure.type) {
        case 'number':
            return translate('pages.growth.measure-values.number', {
                current: measure.current,
                start: measure.start,
                target: measure.target,
                unit: measure.unit,
            });
        case 'percent':
            return translate('pages.growth.measure-values.percent', { value: measure.current });
        case 'milestones':
            return translate('pages.growth.measure-values.milestones', {
                count: measure.milestones.length,
                done: measure.milestones.filter(({ isDone }) => isDone).length,
            });
        case 'done':
            return translate(measure.isDone ? 'pages.growth.states.done' : 'pages.growth.detail.not-done-yet');
    }
};

export const MeasureSummary: FunctionComponent<{
    readonly className?: string;
    readonly measure: GrowthMeasure | null | undefined;
}> = ({ className, measure }) => {
    const translate = useTranslate();

    if (is.nullOrUndefined(measure)) {
        return null;
    }

    const progress = measureProgress(measure);

    return (
        <div className={clsx('flex flex-col gap-s', className)}>
            <span className="text-body-s tabular-nums text-text-secondary">{measureLabel(measure, translate)}</span>
            {progress === undefined ? null : (
                <div className="h-xs w-full overflow-hidden rounded-full bg-surface-hover">
                    <div
                        className="h-full rounded-full bg-surface-inverse transition-[width] duration-300 ease-out"
                        style={{ width: `${Math.round(Math.min(Math.max(progress, 0), 1) * 100)}%` }}
                    />
                </div>
            )}
        </div>
    );
};
